import type { models } from '@org-avp/device-registry';
import { getBrand } from '@org-avp/models';
import { getKvpsDealerId, ThingNameSchema } from 'utils/thingName';
import { localAveTabId } from 'utils/localAveTabId';

/**
 * Backwards-compatible device type that handles various data formats:
 * - New library format: { type: 'AVE', ... }
 * - Raw API format: { type: 'UHD_WALL', clientClass: 'AVE', ... }
 * - Old mock format: { type: 'AVE', streamUrl: '...', ... }
 */
export type DeviceLike = Record<string, unknown>;

/**
 * Type guard to check if a device is an AVE device.
 * Handles both transformed library format and raw API format for resilience.
 *
 * @param device - Device object to check
 * @returns True if device is an AVE type device
 *
 * @example
 * // New library format
 * isAveDevice({ type: 'AVE', id: '123', name: 'Device' }) // true
 *
 * @example
 * // Raw API format (fallback when library transformation fails)
 * isAveDevice({ type: 'UHD_WALL', clientClass: 'AVE', id: '123' }) // true
 *
 * @example
 * // Non-AVE device
 * isAveDevice({ type: 'Chromebox', id: '456' }) // false
 */
export function isAveDevice(device: DeviceLike): device is models.AveDevice {
  // Primary check: transformed library format where type === 'AVE'
  if (device.type === 'AVE') {
    return true;
  }

  // Fallback check: raw API format where clientClass === 'AVE'
  // This handles cases where the device-registry library fails to transform the data
  // The API returns type as screen type (e.g., 'UHD_WALL', 'FHD_WALL') and clientClass as device category
  if ('clientClass' in device && device.clientClass === 'AVE') {
    return true;
  }

  return false;
}

/**
 * Filters an array of devices to only include AVE devices.
 * Backwards-compatible with various data formats.
 *
 * @param devices - Array of device objects
 * @returns Array containing only AVE devices
 */
export function filterAveDevices(devices: DeviceLike[]): models.AveDevice[] {
  return devices.filter(isAveDevice);
}

/**
 * Sorts devices alphabetically by name.
 * Generic function that works with any object having a name property.
 *
 * @param devices - Array of devices to sort
 * @returns New array sorted alphabetically by name
 */
export function sortDevicesByName<T extends { name?: string }>(devices: T[]): T[] {
  return [...devices].sort((a, b) => {
    const nameA = a.name ?? '';
    const nameB = b.name ?? '';
    return nameA.localeCompare(nameB);
  });
}

/**
 * Determines if a device is disabled.
 * A device is considered disabled if it's unavailable AND has no active session.
 *
 * @param device - Device to check
 * @returns True if device is disabled
 */
export function isDeviceDisabled(device: DeviceLike): boolean {
  const available = device.available;
  const sessionId = 'sessionId' in device ? device.sessionId : null;

  return !available && !sessionId;
}

/**
 * Determines whether a device's registry-side session binding belongs to THIS browser tab
 * (i.e. reconnecting is a no-op takeover of the tab's own prior connection, not a foreign one).
 *
 * @param device - Device to check
 * @returns True if the device's sessionId equals this tab's localAveTabId
 */
export function isOwnDeviceBinding(device: DeviceLike): boolean {
  return device.sessionId === localAveTabId;
}

/**
 * Returns true when the bound (stored) device snapshot differs from the live registry entry.
 * Used to detect when a taken-over device's sessionId or availability has changed.
 */
export function isBindingStale(bound: DeviceLike, live: DeviceLike): boolean {
  return bound.sessionId !== live.sessionId || bound.available !== live.available;
}

/**
 * Determines whether a device belongs to the given brand (AVS-4450 D6).
 * The Distribution registry is brand-agnostic, so a dealer's list also carries its other
 * brands' devices, and a context token for those is refused. The brand is the dealer-id
 * letter of the device's thingName: `thingName` on the raw-API shape, else `id`.
 * A multi-brand device is named after its first brand, so it matches that brand only.
 *
 * @param device - Device to check
 * @param brand - Context brand letter (e.g. 'A')
 * @returns True if the thingName's brand matches, or if the id is not a parseable thingName
 */
export function isDeviceOfBrand(device: DeviceLike, brand: string): boolean {
  const parsed = ThingNameSchema.safeParse(device.thingName ?? device.id);
  if (!parsed.success) {
    return true;
  }
  return getBrand(getKvpsDealerId(parsed.data)) === brand;
}

/**
 * Filters devices to AVE types of the given brand and sorts them alphabetically by name.
 *
 * @param devices - Array of devices
 * @param brand - Context brand letter; devices of other brands are dropped. When missing,
 *   no brand filter runs (hiding every device would silently break broadcasting).
 * @returns Filtered and sorted array of AVE devices
 */
export function getFilteredSortedAveDevices(devices: DeviceLike[], brand?: string): models.AveDevice[] {
  const aveDevices = filterAveDevices(devices);
  const brandDevices = brand ? aveDevices.filter((device) => isDeviceOfBrand(device, brand)) : aveDevices;
  return sortDevicesByName(brandDevices);
}
